/**
 * Types for the PUBLIC project-release feed.
 *
 * Mirrors the stable contract written by i3deploy-back to the public GCS bucket
 * (`gs://i3deploy-public`) at `<org>/projects/<project>/releases.json`. It is the
 * curated, published-only, user-facing changelog — no commit SHAs, no deploy
 * events, no technical notes.
 *
 * Contract owner: i3deploy-back `tracker/bucket/manifest.build_project_public_releases`
 * (guide: i3deploy-front `.../releases/PUBLIC_FEED.md`). Breaking changes bump
 * `schema_version` (currently "1.0").
 */

/** One curated, published release as exposed to end users. */
export interface PublicProjectRelease {
  /** Semantic version, e.g. "1.7.0". Stable key + route param. */
  version: string;
  /** Human title of the release. */
  name: string;
  /** ISO-8601 timestamp of when it was published/released. */
  released_at: string;
  /** One-liner for a toast / "what's new" indicator. */
  short_summary: string;
  /** User-facing changelog body (Markdown). */
  changelog_markdown: string;
  /** Who authored the changelog. */
  authored_by: 'ai' | 'human';
  /** Bundled service versions, keyed by service slug, e.g. { "pwd-backend": "1.7.0" }. */
  service_versions: Record<string, string>;
}

/** The full `releases.json` document for one project. */
export interface ProjectReleaseFeed {
  /** Public contract version. Guard against majors we don't support. */
  schema_version: string;
  organization_slug: string;
  project: {
    slug: string;
    display_name: string;
  };
  /** ISO-8601 timestamp of when the file was generated. */
  generated_at: string;
  /** Newest first, published-only, capped at 50. */
  releases: PublicProjectRelease[];
}

/** A release enriched with per-browser read state (from ReleaseNotesStore). */
export interface ReleaseWithReadState extends PublicProjectRelease {
  is_read: boolean;
}

/** i3deploy identifiers for ProWoDo's feed. */
export const RELEASE_FEED_ORG = 'i3';
export const RELEASE_FEED_PROJECT = 'pwd';
